import { buildTensionUserSnapshotForAtlas } from './tension-atlas-user-snapshot';
import { analytics } from './analytics';
import { projectId } from '/utils/supabase/info';
import { getAuthHeadersForProtectedApi } from './db-storage';
import { trackTelemetryEvent } from './telemetry';

export type ConsumerLoginSnapshot = {
  generatedAt: string;
  headline: string;
  summary: string;
  quickAdvice: Array<string>;
  riskLevel: 'low' | 'medium' | 'high';
};

type SnapshotAccuracyContext = {
  activeDaysInAppForRelationship: number | null;
  checkInCountInAppForRelationship: number | null;
};

const CONSUMER_LOGIN_SNAPSHOT_KEY_PREFIX = 'tension_consumer_login_snapshot_v1:';
const CONSUMER_LOGIN_SNAPSHOT_MARKER_PREFIX = 'tension_login_snapshot_generated_v1:';
const CONSUMER_LOGIN_SNAPSHOT_SESSION_KEY = 'tension_login_snapshot_session_v1';
export const CONSUMER_LOGIN_SNAPSHOT_UPDATED_EVENT = 'tension_login_snapshot_updated';
const CONSUMER_LOGIN_SNAPSHOT_SLO_WINDOW_PREFIX = 'tension_login_snapshot_slo_window_v1:';
const CONSUMER_LOGIN_SNAPSHOT_SCHEMA_VERSION_DEFAULT = 'consumer_login_snapshot_v1';
const CONSUMER_LOGIN_SNAPSHOT_RETENTION_DAYS_DEFAULT = 30;
const CONSUMER_LOGIN_SNAPSHOT_FRESH_MS_DEFAULT = 2 * 60 * 60 * 1000;
const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4`;
const inFlightSnapshotRequests = new Map<string, Promise<ConsumerLoginSnapshot>>();

function safeParseJson<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function normalizeSnapshot(raw: Record<string, unknown> | null): ConsumerLoginSnapshot | null {
  if (!raw) return null;
  const headline = typeof raw.headline === 'string' ? raw.headline.trim() : '';
  const summary = typeof raw.summary === 'string' ? raw.summary.trim() : '';
  const riskRaw = typeof raw.riskLevel === 'string' ? raw.riskLevel.trim().toLowerCase() : '';
  const quickAdvice = Array.isArray(raw.quickAdvice)
    ? raw.quickAdvice
        .map((line) => (typeof line === 'string' ? line.trim() : ''))
        .filter((line) => line.length > 0)
        .slice(0, 5)
    : [];
  const riskLevel: ConsumerLoginSnapshot['riskLevel'] =
    riskRaw === 'high' ? 'high' : riskRaw === 'medium' ? 'medium' : 'low';

  if (!headline || !summary || quickAdvice.length === 0) return null;
  return {
    generatedAt: new Date().toISOString(),
    headline,
    summary,
    quickAdvice,
    riskLevel,
  };
}

function fallbackSnapshot(): ConsumerLoginSnapshot {
  return {
    generatedAt: new Date().toISOString(),
    headline: 'Quick login snapshot ready',
    summary: 'Keep momentum with one focused check-in and one calm communication step today.',
    quickAdvice: [
      'Run a daily check-in now to update your baseline.',
      'Use one short, low-pressure reconnection message.',
      'Open Smart Insights and complete one suggested action.',
    ],
    riskLevel: 'medium',
  };
}

function parsePositiveInt(value: unknown): number | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.floor(parsed);
}

function getSnapshotAccuracyContext(
  userTensionSnapshot: Record<string, unknown> | null | undefined,
): SnapshotAccuracyContext {
  const checkIns =
    userTensionSnapshot && typeof userTensionSnapshot.checkIns === 'object'
      ? (userTensionSnapshot.checkIns as Record<string, unknown>)
      : null;
  return {
    activeDaysInAppForRelationship: parsePositiveInt(checkIns?.activeDaysInAppForRelationship),
    checkInCountInAppForRelationship: parsePositiveInt(checkIns?.countInAppForRelationship),
  };
}

function maxDayClaim(snapshot: ConsumerLoginSnapshot): number {
  const corpus = [snapshot.headline, snapshot.summary, ...(snapshot.quickAdvice || [])]
    .map((line) => String(line || ''))
    .join(' ');
  const regex = /(\d{1,4})\s*[- ]?\s*days?\b/gi;
  let maxClaim = 0;
  let match: RegExpExecArray | null = regex.exec(corpus);
  while (match) {
    const n = Number(match[1]);
    if (Number.isFinite(n)) {
      maxClaim = Math.max(maxClaim, n);
    }
    match = regex.exec(corpus);
  }
  return maxClaim;
}

function isSnapshotPlausible(
  snapshot: ConsumerLoginSnapshot,
  ctx: SnapshotAccuracyContext,
): boolean {
  const claimedDays = maxDayClaim(snapshot);
  if (claimedDays <= 0) return true;

  if (
    ctx.activeDaysInAppForRelationship !== null &&
    claimedDays > Math.max(1, ctx.activeDaysInAppForRelationship)
  ) {
    return false;
  }

  if (
    ctx.checkInCountInAppForRelationship !== null &&
    claimedDays > Math.max(1, ctx.checkInCountInAppForRelationship)
  ) {
    return false;
  }

  return true;
}

function snapshotStorageKey(userId: string): string {
  return `${CONSUMER_LOGIN_SNAPSHOT_KEY_PREFIX}${userId}`;
}

function getLoginSnapshotSessionId(userId: string): string {
  const fallback = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = sessionStorage.getItem(CONSUMER_LOGIN_SNAPSHOT_SESSION_KEY);
    const parsed = safeParseJson<{ userId?: string; sessionId?: string }>(raw);
    if (parsed?.userId === userId && typeof parsed.sessionId === 'string' && parsed.sessionId.length > 0) {
      return parsed.sessionId;
    }
    sessionStorage.setItem(
      CONSUMER_LOGIN_SNAPSHOT_SESSION_KEY,
      JSON.stringify({ userId, sessionId: fallback }),
    );
    return fallback;
  } catch {
    return fallback;
  }
}

export function readLatestConsumerLoginSnapshot(userId: string | null | undefined): ConsumerLoginSnapshot | null {
  if (typeof window === 'undefined' || !userId) return null;
  pruneExpiredConsumerLoginSnapshots();
  const parsed = safeParseJson<ConsumerLoginSnapshot>(localStorage.getItem(snapshotStorageKey(userId)));
  if (!parsed) return null;
  if (
    typeof parsed.generatedAt !== 'string' ||
    typeof parsed.headline !== 'string' ||
    typeof parsed.summary !== 'string' ||
    !Array.isArray(parsed.quickAdvice)
  ) {
    return null;
  }
  return parsed;
}

export function clearLatestConsumerLoginSnapshot(userId: string | null | undefined): void {
  if (typeof window === 'undefined' || !userId) return;
  localStorage.removeItem(snapshotStorageKey(userId));
}

export function clearAllConsumerLoginSnapshots(): void {
  if (typeof window === 'undefined') return;
  const keysToRemove: Array<string> = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CONSUMER_LOGIN_SNAPSHOT_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  } catch {
    // ignore restricted storage environments
  }
}

export function clearLoginSnapshotSessionState(): void {
  if (typeof window === 'undefined') return;
  const keysToRemove: Array<string> = [];
  try {
    sessionStorage.removeItem(CONSUMER_LOGIN_SNAPSHOT_SESSION_KEY);
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(CONSUMER_LOGIN_SNAPSHOT_MARKER_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      sessionStorage.removeItem(key);
    }
  } catch {
    // ignore restricted storage environments
  }
}

export function shouldGenerateLoginSnapshotForSession(userId: string): boolean {
  if (typeof window === 'undefined') return false;
  const marker = `${CONSUMER_LOGIN_SNAPSHOT_MARKER_PREFIX}${userId}:${getLoginSnapshotSessionId(userId)}`;
  try {
    if (sessionStorage.getItem(marker) === 'true') {
      return false;
    }
    sessionStorage.setItem(marker, 'true');
    return true;
  } catch {
    // If sessionStorage is restricted, allow best-effort generation.
    return true;
  }
}

function saveLatestSnapshot(userId: string, snapshot: ConsumerLoginSnapshot): void {
  if (typeof window === 'undefined' || !userId) return;
  pruneExpiredConsumerLoginSnapshots();
  localStorage.setItem(snapshotStorageKey(userId), JSON.stringify(snapshot));
  window.dispatchEvent(
    new CustomEvent(CONSUMER_LOGIN_SNAPSHOT_UPDATED_EVENT, { detail: { userId, snapshot } }),
  );
}

type SnapshotSloConfig = {
  warnMs: number;
  failMs: number;
  failureRateAlertThreshold: number;
};

function getSnapshotSloConfig(): SnapshotSloConfig {
  const warnRaw = Number(
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem('tension_consumer_login_snapshot_latency_warn_ms')
      : null) || '3500',
  );
  const failRaw = Number(
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem('tension_consumer_login_snapshot_latency_fail_ms')
      : null) || '8000',
  );
  const thresholdRaw = Number(
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem('tension_consumer_login_snapshot_failure_rate_alert_threshold')
      : null) || '0.2',
  );
  return {
    warnMs: Number.isFinite(warnRaw) ? warnRaw : 3500,
    failMs: Number.isFinite(failRaw) ? failRaw : 8000,
    failureRateAlertThreshold: Number.isFinite(thresholdRaw) ? thresholdRaw : 0.2,
  };
}

function readExpectedSchemaVersion(): string {
  const raw =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('tension_consumer_login_snapshot_schema_version')
      : null;
  return raw && raw.trim().length > 0 ? raw.trim() : CONSUMER_LOGIN_SNAPSHOT_SCHEMA_VERSION_DEFAULT;
}

function getSnapshotRetentionDays(): number {
  const raw =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('tension_consumer_login_snapshot_retention_days')
      : null;
  const parsed = Number(raw || String(CONSUMER_LOGIN_SNAPSHOT_RETENTION_DAYS_DEFAULT));
  if (!Number.isFinite(parsed) || parsed < 1) {
    return CONSUMER_LOGIN_SNAPSHOT_RETENTION_DAYS_DEFAULT;
  }
  return Math.floor(parsed);
}

function getSnapshotFreshMs(): number {
  const raw =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('tension_consumer_login_snapshot_fresh_ms')
      : null;
  const parsed = Number(raw || String(CONSUMER_LOGIN_SNAPSHOT_FRESH_MS_DEFAULT));
  if (!Number.isFinite(parsed) || parsed < 1_000) {
    return CONSUMER_LOGIN_SNAPSHOT_FRESH_MS_DEFAULT;
  }
  return Math.floor(parsed);
}

function isSnapshotFresh(snapshot: ConsumerLoginSnapshot | null): boolean {
  if (!snapshot?.generatedAt) return false;
  const generatedAtMs = Date.parse(snapshot.generatedAt);
  if (!Number.isFinite(generatedAtMs)) return false;
  return Date.now() - generatedAtMs <= getSnapshotFreshMs();
}

function pruneExpiredConsumerLoginSnapshots(): void {
  if (typeof window === 'undefined') return;
  const retentionMs = getSnapshotRetentionDays() * 24 * 60 * 60 * 1000;
  const now = Date.now();
  const keysToRemove: Array<string> = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(CONSUMER_LOGIN_SNAPSHOT_KEY_PREFIX)) continue;
      const parsed = safeParseJson<ConsumerLoginSnapshot>(localStorage.getItem(key));
      const generatedAt = parsed?.generatedAt ? Date.parse(parsed.generatedAt) : Number.NaN;
      if (!Number.isFinite(generatedAt) || now - generatedAt > retentionMs) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  } catch {
    // ignore restricted storage environments
  }
}

async function emitSnapshotSloTelemetry(payload: Record<string, unknown>): Promise<void> {
  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeadersForProtectedApi(),
  };
  try {
    await trackTelemetryEvent('consumer_login_snapshot_slo_alert', payload);
  } catch {
    // ignore telemetry sink failures
  }
  try {
    await fetch(`${API_BASE}/telemetry/consumer-snapshot-slo`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
  } catch {
    // ignore server ingest failures
  }
}

async function recordSnapshotSloOutcome(args: {
  userId: string;
  latencyMs: number;
  failed: boolean;
  reason?: string;
}): Promise<void> {
  const config = getSnapshotSloConfig();
  const windowKey = `${CONSUMER_LOGIN_SNAPSHOT_SLO_WINDOW_PREFIX}${args.userId}`;
  try {
    const previous =
      safeParseJson<Array<{ failed: boolean; ts: number }>>(sessionStorage.getItem(windowKey)) ?? [];
    const next = [...previous.slice(-19), { failed: args.failed, ts: Date.now() }];
    sessionStorage.setItem(windowKey, JSON.stringify(next));
    const failures = next.filter((row) => row.failed).length;
    const failureRate = next.length > 0 ? failures / next.length : 0;

    if (args.latencyMs >= config.warnMs) {
      await emitSnapshotSloTelemetry({
        kind: args.latencyMs >= config.failMs ? 'latency_fail' : 'latency_warn',
        latencyMs: args.latencyMs,
        warnMs: config.warnMs,
        failMs: config.failMs,
        reason: args.reason || null,
      });
    }
    if (failureRate >= config.failureRateAlertThreshold) {
      await emitSnapshotSloTelemetry({
        kind: 'failure_rate_alert',
        failureRate,
        sampleSize: next.length,
        threshold: config.failureRateAlertThreshold,
        reason: args.reason || null,
      });
    }
  } catch {
    // ignore storage restrictions
  }
}

export async function generateConsumerLoginSnapshot(userId: string): Promise<ConsumerLoginSnapshot> {
  const existing = readLatestConsumerLoginSnapshot(userId);
  if (isSnapshotFresh(existing)) {
    analytics.track('login_snapshot_generated', {
      latencyMs: 0,
      riskLevel: existing.riskLevel,
      usedFallback: false,
      schemaVersion: readExpectedSchemaVersion(),
      expectedSchemaVersion: readExpectedSchemaVersion(),
      schemaVersionMismatch: false,
      quickAdviceCount: existing.quickAdvice.length,
      source: 'cache_fresh',
    });
    return existing;
  }

  const inFlight = inFlightSnapshotRequests.get(userId);
  if (inFlight) {
    return inFlight;
  }

  const requestPromise = generateConsumerLoginSnapshotInner(userId);
  inFlightSnapshotRequests.set(userId, requestPromise);
  try {
    return await requestPromise;
  } finally {
    inFlightSnapshotRequests.delete(userId);
  }
}

async function generateConsumerLoginSnapshotInner(userId: string): Promise<ConsumerLoginSnapshot> {
  const startedAt = Date.now();
  const userTensionSnapshot = buildTensionUserSnapshotForAtlas();
  const accuracyContext = getSnapshotAccuracyContext(userTensionSnapshot);
  const expectedSchemaVersion = readExpectedSchemaVersion();
  const timeoutMs = 8000;

  try {
    const response = await Promise.race([
      fetch(`${API_BASE}/ai/login-snapshot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeadersForProtectedApi(),
        },
        body: JSON.stringify({
          context: {
            userTensionSnapshot,
          },
        }),
      }),
      new Promise<Response>((_, reject) =>
        window.setTimeout(() => reject(new Error('login_snapshot_timeout')), timeoutMs),
      ),
    ]);
    if (!response.ok) {
      throw new Error(`login_snapshot_http_${response.status}`);
    }
    const payload = (await response.json()) as Record<string, unknown>;
    const schemaVersion =
      typeof payload.schemaVersion === 'string' ? payload.schemaVersion : CONSUMER_LOGIN_SNAPSHOT_SCHEMA_VERSION_DEFAULT;
    const normalizedBase = normalizeSnapshot(
      payload.snapshot && typeof payload.snapshot === 'object'
        ? (payload.snapshot as Record<string, unknown>)
        : null,
    );
    const normalized =
      normalizedBase && isSnapshotPlausible(normalizedBase, accuracyContext)
        ? normalizedBase
        : null;
    const versionMismatch = schemaVersion !== expectedSchemaVersion;
    const snapshot = normalized || fallbackSnapshot();
    saveLatestSnapshot(userId, snapshot);
    const latencyMs = Date.now() - startedAt;
    await recordSnapshotSloOutcome({
      userId,
      latencyMs,
      failed: false,
      reason: versionMismatch
        ? 'schema_version_mismatch'
        : normalized == null
          ? 'contract_fallback'
          : undefined,
    });
    analytics.track('login_snapshot_generated', {
      latencyMs,
      riskLevel: snapshot.riskLevel,
      usedFallback: normalized == null,
      schemaVersion,
      expectedSchemaVersion,
      schemaVersionMismatch: versionMismatch,
      quickAdviceCount: snapshot.quickAdvice.length,
    });
    return snapshot;
  } catch (error) {
    const snapshot = fallbackSnapshot();
    saveLatestSnapshot(userId, snapshot);
    const latencyMs = Date.now() - startedAt;
    await recordSnapshotSloOutcome({
      userId,
      latencyMs,
      failed: true,
      reason: error instanceof Error ? error.message : 'unknown_error',
    });
    analytics.track('login_snapshot_failed', {
      latencyMs,
      fallbackUsed: true,
      reason: error instanceof Error ? error.message : String(error),
    });
    return snapshot;
  }
}
